<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isValidVariableKey } from './variableHelpers';

const props = defineProps({
  variables: {
    type: Array,
    required: true,
    validator: value => value.every(variable => 'key' in variable),
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const search = ref('');

const visibleVariables = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return props.variables;
  return props.variables.filter(variable =>
    variable.key.toLowerCase().includes(query)
  );
});

const typedKey = computed(() => search.value.trim());

const isDuplicate = computed(() =>
  props.variables.some(variable => variable.key === typedKey.value)
);

const canCreateFromSearch = computed(
  () =>
    typedKey.value && !isDuplicate.value && isValidVariableKey(typedKey.value)
);

// Only worth complaining once the user typed something that can never become a variable -
// a name that just doesn't exist yet is the normal path, not an error.
const invalidNameMessage = computed(() =>
  typedKey.value && !isDuplicate.value && !isValidVariableKey(typedKey.value)
    ? t('FLOWS.EDITOR.VARIABLES.INVALID_NAME')
    : ''
);

const selectVariable = key => {
  emit('select', key);
  search.value = '';
};

// Built in JS rather than written literally in the template: a `{{` right next to a Vue
// mustache confuses the template compiler's delimiter scanning.
const variableLabel = key => ['{{', key, '}}'].join('');
</script>

<template>
  <div class="flex flex-col gap-3 min-h-0">
    <div class="relative flex-shrink-0">
      <span
        class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
      />
      <input
        v-model="search"
        type="search"
        :placeholder="t('FLOWS.EDITOR.VARIABLES.SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 !pl-9 !pr-3 text-sm rounded-lg outline outline-1 outline-n-weak focus:outline-n-brand bg-n-solid-1 text-n-slate-12"
        @keyup.enter="canCreateFromSearch && selectVariable(typedKey)"
      />
    </div>

    <div class="flex flex-col gap-1 overflow-y-auto min-h-0">
      <button
        v-for="variable in visibleVariables"
        :key="variable.key"
        type="button"
        class="flex items-center justify-between gap-2 w-full text-left px-2 py-2 rounded-md hover:bg-n-slate-3"
        @click="selectVariable(variable.key)"
      >
        <span class="text-sm font-mono text-n-slate-12 truncate">
          {{ variableLabel(variable.key) }}
        </span>
        <span
          v-if="variable.usedIn?.length"
          class="flex-shrink-0 text-[10px] text-n-slate-10 truncate"
        >
          {{
            t('FLOWS.EDITOR.VARIABLES.USED_IN', {
              count: variable.usedIn.length,
            })
          }}
        </span>
      </button>

      <button
        v-if="canCreateFromSearch"
        type="button"
        class="flex items-center gap-2 w-full text-left px-2 py-2 rounded-md text-n-blue-11 hover:bg-n-slate-3"
        @click="selectVariable(typedKey)"
      >
        <span class="i-lucide-plus flex-shrink-0" />
        <span class="text-sm truncate">
          {{ t('FLOWS.EDITOR.VARIABLES.CREATE', { key: typedKey }) }}
        </span>
      </button>

      <p v-if="invalidNameMessage" class="px-2 py-2 text-xs text-n-ruby-11">
        {{ invalidNameMessage }}
      </p>

      <p
        v-else-if="!visibleVariables.length && !canCreateFromSearch"
        class="px-2 py-4 text-sm text-center text-n-slate-10"
      >
        {{ t('FLOWS.EDITOR.VARIABLES.NO_RESULTS') }}
      </p>
    </div>
  </div>
</template>
